import React, { useEffect, useState } from "react";
import guideThinkHomeImg from "../assets/illustrations/quick-clarity/qc-guide-home-think.png";
import guideWriteHomeImg from "../assets/illustrations/quick-clarity/qc-guide-home-write.png";
import guideReadyHomeImg from "../assets/illustrations/quick-clarity/qc-guide-home-action.png";

const HERO_HEADLINE = "Stop reacting. Start thinking clearly.";
const SAMPLE_ENTRY = "They didn't reply. I feel rejected.";
const SLIDE_MS = 5600;

const SLIDES = [
  {
    key: "think",
    title: "What's taking space in your mind?",
    body: "A looping thought, conversation, or decision shows up. Name it before it drives the next reaction.",
    scene: "think",
    character: guideThinkHomeImg
  },
  {
    key: "examine",
    title: "Write it. Examine this thought.",
    body: "Put it in Quick Clarity, then tap Examine this thought — your words stay as you entered them.",
    scene: "examine",
    character: guideWriteHomeImg
  },
  {
    key: "clarity",
    title: "Leave ready to respond",
    body: "See facts vs interpretation, one primary insight, and a deliberate next response — then act on purpose, not autopilot.",
    scene: "clarity",
    character: guideReadyHomeImg
  }
];

const focusQuickClarityEntry = () => {
  const field = document.getElementById("gratitude-entry");
  if (!field) return;
  field.scrollIntoView({ behavior: "smooth", block: "center" });
  window.setTimeout(() => {
    field.focus({ preventScroll: true });
  }, 280);
};

const CarouselSceneContent = ({ scene }) => {
  if (scene === "think") {
    return (
      <div className="qc-carousel__scene-slot qc-carousel__scene-slot--empty" aria-hidden="true" />
    );
  }

  if (scene === "examine") {
    return (
      <div className="qc-carousel__scene-slot">
        <div className="qc-carousel__product" aria-hidden="true">
          <p className="qc-carousel__product-label">Quick Clarity</p>
          <div className="qc-carousel__product-box">
            <p className="qc-carousel__product-entry">{SAMPLE_ENTRY}</p>
          </div>
          <div className="qc-carousel__product-cta">Examine this thought</div>
        </div>
      </div>
    );
  }

  return (
    <div className="qc-carousel__scene-slot">
      <div className="qc-carousel__results">
        <div className="qc-carousel__result" aria-hidden="true">
          <span className="qc-carousel__result-kicker">Your interpretation</span>
          <span>The lack of reply is interpreted as a personal rejection.</span>
        </div>
        <div className="qc-carousel__result qc-carousel__result--next" aria-hidden="true">
          <span className="qc-carousel__result-kicker">Next response</span>
          <span>Decide the next step from evidence, not the first reaction.</span>
        </div>
        <button type="button" className="qc-carousel__try-now" onClick={focusQuickClarityEntry}>
          Try Now For Free - Now!!
        </button>
      </div>
    </div>
  );
};

const HomeHero = () => {
  const [titleChars, setTitleChars] = useState(0);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const id = window.setInterval(() => {
      setActive((i) => (i + 1) % SLIDES.length);
    }, SLIDE_MS);
    return () => window.clearInterval(id);
  }, [paused]);

  useEffect(() => {
    setTitleChars(0);
  }, [active]);

  useEffect(() => {
    const full = SLIDES[active].title;
    if (titleChars >= full.length) return undefined;
    const id = window.setTimeout(() => setTitleChars((n) => n + 1), 36);
    return () => window.clearTimeout(id);
  }, [active, titleChars]);

  const slide = SLIDES[active];
  const titleText = slide.title.slice(0, titleChars);

  return (
    <section className="hero-v2" aria-label="Hero">
      <p className="hero-v2__badge">For high-performing minds</p>
      <h1 className="hero-v2__headline">{HERO_HEADLINE}</h1>

      <div
        className="qc-carousel"
        aria-roledescription="carousel"
        aria-label="How Quick Clarity helps"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
      >
        <div className={`qc-carousel__frame qc-carousel__frame--${slide.scene}`} aria-live="polite">
          <div className="qc-carousel__character-wrap">
            {slide.scene === "think" ? (
              <div className="qc-carousel__thoughts qc-carousel__thoughts--over-head" aria-hidden="true">
                <span className="qc-carousel__bubble qc-carousel__bubble--a" />
                <span className="qc-carousel__bubble qc-carousel__bubble--b" />
                <span className="qc-carousel__bubble qc-carousel__bubble--c" />
              </div>
            ) : null}
            <img
              key={slide.scene}
              src={slide.character}
              alt=""
              className={`qc-carousel__character qc-carousel__character--${slide.scene}`}
              width={420}
              height={560}
              decoding="async"
            />
          </div>

          <div className={`qc-carousel__content qc-carousel__content--${slide.scene}`}>
            <h2 className="qc-carousel__title" aria-label={slide.title}>
              <span aria-hidden="true">
                {titleText}
                <span className="qc-carousel__title-caret" />
              </span>
            </h2>
            <CarouselSceneContent scene={slide.scene} />
            <p className="qc-carousel__body">{slide.body}</p>
          </div>
        </div>

        <div className="qc-carousel__dots" role="tablist" aria-label="Quick Clarity story steps">
          {SLIDES.map((item, index) => (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={index === active}
              aria-label={item.title}
              className={`qc-carousel__dot${index === active ? " qc-carousel__dot--active" : ""}`}
              onClick={() => setActive(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomeHero;
